import { useState } from "react";
import {
  AlertCircle, Calendar, Check, Clock, ExternalLink, FileText, Image as ImageIcon,
  MessageSquare, X,
} from "lucide-react";
import { useRoleStore } from "@/shared/store/role";
import {
  useBloggerInbox, useAcceptPlacement, useDeclinePlacement,
  useRequestChanges, type BloggerPlacement,
} from "@/shared/api/wb";
import { Avatar, PlatformIcon, Skeleton, type Platform } from "@/shared/ui";
import { fmtDate, fmtRub } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { ReportModal } from "./ReportModal";
import { ScriptModal } from "./ScriptModal";

const GROUP_ORDER = [
  "invited", "talking", "script_review", "scheduled", "review",
  "published", "done", "declined", "cancelled",
] as const;

export function BloggerInboxPage() {
  const activeBloggerId = useRoleStore((s) => s.activeBloggerId);
  const { data, isLoading } = useBloggerInbox(activeBloggerId);
  const accept = useAcceptPlacement();
  const [reportFor, setReportFor] = useState<BloggerPlacement | null>(null);
  const [scriptFor, setScriptFor] = useState<BloggerPlacement | null>(null);
  const [declineFor, setDeclineFor] = useState<BloggerPlacement | null>(null);
  const [changesFor, setChangesFor] = useState<BloggerPlacement | null>(null);

  if (!activeBloggerId) {
    return (
      <div className="max-w-[640px] mx-auto px-6 py-16 text-center text-ink-40">
        Выберите блогера в шапке, чтобы увидеть его заявки.
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="max-w-[880px] mx-auto px-6 py-8 grid gap-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );
  }

  const { blogger, placements } = data;
  const byStatus = new Map<string, BloggerPlacement[]>();
  for (const p of placements) {
    const list = byStatus.get(p.status) ?? [];
    list.push(p);
    byStatus.set(p.status, list);
  }
  const newCount = (byStatus.get("invited") ?? []).length;

  return (
    <div className="max-w-[880px] mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-1">
        <Avatar name={blogger.full} size={40} grad={blogger.grad as [string, string]} />
        <div>
          <div className="flex items-center gap-1.5 font-display font-extrabold text-[22px] tracking-tight">
            <PlatformIcon platform={blogger.platform as Platform} size={16} /> {blogger.name}
          </div>
          <div className="text-[12.5px] text-ink-40">{blogger.full}</div>
        </div>
      </div>
      <p className="text-ink-40 text-[13.5px] mt-2 mb-7">
        {newCount > 0 ? `${newCount} ${pluralNew(newCount)} ждут ответа` : "Новых заявок нет"} · {placements.length} всего
      </p>

      <div className="grid gap-6">
        {GROUP_ORDER.filter((s) => byStatus.has(s)).map((status) => (
          <div key={status}>
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40 mb-2.5">
              {status === "cancelled" ? "Отменено рекламодателем" : data.columns.find((c) => c.id === status)?.label ?? status}
              <span className="ml-1.5 font-mono normal-case">{byStatus.get(status)!.length}</span>
            </div>
            <div className="grid gap-2.5">
              {byStatus.get(status)!.map((p) => (
                <PlacementCard
                  key={p.id}
                  p={p}
                  onAccept={() => accept.mutate({ bloggerId: blogger.id, placementId: p.id })}
                  onDecline={() => setDeclineFor(p)}
                  onRequestChanges={() => setChangesFor(p)}
                  onSubmitScript={() => setScriptFor(p)}
                  onSubmitReport={() => setReportFor(p)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {reportFor && (
        <ReportModal
          bloggerId={blogger.id}
          placement={reportFor}
          onClose={() => setReportFor(null)}
        />
      )}
      {scriptFor && (
        <ScriptModal
          bloggerId={blogger.id}
          placement={scriptFor}
          onClose={() => setScriptFor(null)}
        />
      )}
      {declineFor && (
        <DeclineModal
          bloggerId={blogger.id}
          placement={declineFor}
          onClose={() => setDeclineFor(null)}
        />
      )}
      {changesFor && (
        <RequestChangesModal
          bloggerId={blogger.id}
          placement={changesFor}
          onClose={() => setChangesFor(null)}
        />
      )}
    </div>
  );
}

function PlacementCard({ p, onAccept, onDecline, onRequestChanges, onSubmitScript, onSubmitReport }: {
  p: BloggerPlacement;
  onAccept: () => void;
  onDecline: () => void;
  onRequestChanges: () => void;
  onSubmitScript: () => void;
  onSubmitReport: () => void;
}) {
  const isNative = p.campaign_type === "native";
  const canAcceptOrDecline = p.status === "invited";
  const canRequestChanges = ["invited", "talking", "scheduled"].includes(p.status);
  // Native must submit a script before it can ever reach 'scheduled' — see
  // submit_report's allowed-statuses guard in blogger_portal.py, which is
  // ("scheduled",) for native vs ("scheduled","talking") for seeding.
  const canSubmitScript = isNative && p.status === "talking";
  const canSubmitReport = isNative ? p.status === "scheduled" : ["talking", "scheduled"].includes(p.status);
  const isDeclined = p.status === "declined";
  const isCancelled = p.status === "cancelled";
  const isDone = p.status === "done";
  const isReview = p.status === "review";
  const isScriptReview = p.status === "script_review";

  return (
    <div className={cn(
      "bg-white rounded-xl border p-4 grid gap-3",
      isDeclined || isCancelled ? "border-danger/20 opacity-70" : isDone ? "border-lime" : "border-ink-12",
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-[15px] truncate">{p.campaign_product}</div>
          <div className="flex items-center gap-2 text-[12px] text-ink-40 mt-0.5">
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtDate(p.publish_date)}</span>
            <span>·</span>
            <span>{isNative ? "Натив" : "Посев"}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-mono font-bold text-[16px]">{fmtRub(p.price)}</div>
          {isDone && <div className="text-[11px] text-success font-display font-semibold">Оплачено</div>}
        </div>
      </div>

      {p.is_overdue && (
        <div className="flex items-center gap-1.5 px-2.5 py-2 bg-warning/10 text-ink rounded-md text-[12px] font-display font-semibold">
          <Clock size={12} className="text-warning flex-shrink-0" /> Просрочено согласование сценария
        </div>
      )}

      {p.msg && <div className="text-[12.5px] text-ink-60 leading-relaxed">{p.msg}</div>}

      {isDeclined && p.decline_reason && (
        <div className="flex items-start gap-1.5 px-2.5 py-2 bg-danger/10 text-danger rounded-md text-[12px]">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {p.decline_reason}
        </div>
      )}

      {isCancelled && p.cancel_reason && (
        <div className="flex items-start gap-1.5 px-2.5 py-2 bg-danger/10 text-danger rounded-md text-[12px]">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {p.cancel_reason}
        </div>
      )}

      {isScriptReview && (
        <div className="px-2.5 py-2 bg-cream rounded-md text-[12px] grid gap-1">
          <div className="flex items-center gap-1.5 text-ink-40"><FileText size={12} /> Сценарий на согласовании у рекламодателя</div>
          {p.script_draft && <div className="text-ink-60 line-clamp-3">{p.script_draft}</div>}
        </div>
      )}

      {isReview && p.report_link && (
        <div className="px-2.5 py-2 bg-cream rounded-md text-[12px] grid gap-1">
          <div className="text-ink-40">Отчёт отправлен · ждём подтверждения</div>
          <a href={p.report_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-link hover:underline truncate">
            <ExternalLink size={11} /> {p.report_link}
          </a>
        </div>
      )}

      {(canAcceptOrDecline || canRequestChanges || canSubmitScript || canSubmitReport) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {canAcceptOrDecline && (
            <button onClick={onAccept}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[12.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
              <Check size={12} strokeWidth={3} /> Принять в работу
            </button>
          )}
          {canSubmitScript && (
            <button onClick={onSubmitScript}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-ink text-white text-[12.5px] font-display font-semibold hover:bg-[#2A2A2A]">
              <FileText size={12} /> Отправить сценарий
            </button>
          )}
          {canSubmitReport && (
            <button onClick={onSubmitReport}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-ink text-white text-[12.5px] font-display font-semibold hover:bg-[#2A2A2A]">
              <ImageIcon size={12} /> Отправить отчёт
            </button>
          )}
          {canRequestChanges && (
            <button onClick={onRequestChanges}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink">
              <MessageSquare size={12} /> Запросить правки
            </button>
          )}
          {canAcceptOrDecline && (
            <button onClick={onDecline}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-danger/10 text-danger text-[12.5px] font-display font-semibold hover:bg-danger/20">
              <X size={12} /> Отклонить
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DeclineModal({ bloggerId, placement, onClose }: {
  bloggerId: string; placement: BloggerPlacement; onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const decline = useDeclinePlacement();

  function submit() {
    decline.mutate({ bloggerId, placementId: placement.id, body: { reason: reason.trim() || undefined } }, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center" style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-lg fade-up p-6 grid gap-4" style={{ width: "min(440px, 92vw)" }}>
        <h3 className="font-display font-extrabold text-[19px] tracking-tight">Отклонить «{placement.campaign_product}»?</h3>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Причина отказа (необязательно)"
          rows={3}
          className="w-full px-3 py-2.5 rounded-md border border-ink-12 text-[13.5px] outline-none focus:border-ink resize-none"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-4 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink/40">Отмена</button>
          <button onClick={submit} disabled={decline.isPending}
            className="h-9 px-4 rounded-pill bg-danger text-white text-[13px] font-display font-semibold hover:opacity-90 disabled:opacity-50">
            Отклонить
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestChangesModal({ bloggerId, placement, onClose }: {
  bloggerId: string; placement: BloggerPlacement; onClose: () => void;
}) {
  const [message, setMessage] = useState("");
  const requestChanges = useRequestChanges();

  function submit() {
    if (!message.trim()) return;
    requestChanges.mutate({ bloggerId, placementId: placement.id, body: { message: message.trim() } }, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center" style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-lg fade-up p-6 grid gap-4" style={{ width: "min(440px, 92vw)" }}>
        <h3 className="font-display font-extrabold text-[19px] tracking-tight">Запросить правки</h3>
        <p className="text-[12.5px] text-ink-40 -mt-2">Сообщение уйдёт рекламодателю в чат по «{placement.campaign_product}».</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Что нужно уточнить или поправить?"
          rows={3}
          autoFocus
          className="w-full px-3 py-2.5 rounded-md border border-ink-12 text-[13.5px] outline-none focus:border-ink resize-none"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-4 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink/40">Отмена</button>
          <button onClick={submit} disabled={requestChanges.isPending || !message.trim()}
            className="h-9 px-4 rounded-pill bg-ink text-white text-[13px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-50">
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
}

function pluralNew(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "заявка";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "заявки";
  return "заявок";
}
